import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { handleLogoutStorage } from '../../utils/authStorage';
import drop_down from '../../../shared/assets/svg/drop_down_white.svg';
import socket from '../../../../socket';
import toast from 'react-hot-toast';
import {
  getUserById,
  getUserCompanyIds
} from '../../../builder/services/api/implementation/impl';
import { logoutCurrentSession } from '../../services/api/implementation/impl';
import logoIcon from '../../../shared/assets/svg/LOGO-ICON.svg';
import { ChevronRight } from 'lucide-react';
import { ownedByWorkforce } from '../../utils/sidebarOwnership';

const Sidebar = ({ role, links, logo, collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const DesignationName = Cookies.get('userDesignation'); // Getting the designation from cookies
  const userVertical = Cookies.get('userVertical'); // Getting the vertical from cookies

  const [selectedPath, setSelectedPath] = useState(location.pathname);
  const [expandedItem, setExpandedItem] = useState('');

  /**
   * Hover intent, not hover.
   *
   * The sidebar sits against the left edge, so a cursor crossing the screen
   * passes through it. Expanding on the first mouseenter meant the panel and
   * whichever group was open jumped out at somebody who was on their way
   * somewhere else. A short dwell separates "going past" from "going in".
   */
  const hoverTimer = useRef(null);

  const cancelHoverOpen = useCallback(() => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
    if (role !== 'admin' && setCollapsed) setCollapsed(true);
  }, [role, setCollapsed]);

  const openOnHover = useCallback(() => {
    if (role === 'admin' || !setCollapsed) return;
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      hoverTimer.current = null;
      setCollapsed(false);
    }, 220);
  }, [role, setCollapsed]);

  useEffect(
    () => () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
    },
    []
  );

  const handleLogout = useCallback(async () => {
    try {
      await logoutCurrentSession();
    } catch (err) {
      // Still clear local session even if API fails
      console.error('Logout API failed:', err);
    }
    handleLogoutStorage();
    // Leave this user's rooms so the next login in this tab starts clean.
    socket.disconnect();
    navigate('/');
  }, [navigate]);

  const handleLinkClick = useCallback(
    (path) => {
      if (path === 'logout') {
        handleLogout();
      } else {
        setSelectedPath(path);
        navigate(path);
      }
    },
    [navigate, handleLogout]
  );

  useEffect(() => {
    setSelectedPath(location.pathname);
    const parent = links.find(
      (link) => link.children?.length && isParentActive(location.pathname, link)
    );
    if (parent) {
      setExpandedItem(parent.name);
    }
  }, [location.pathname, links]);

  useEffect(() => {
    const handlePost = async (res) => {
      try {
        const userId = Cookies.get('userId');
        if (!userId) return;

        const userResponse = await getUserById(userId);
        const companyIds = getUserCompanyIds(userResponse);
        const postCompanyId = Number(res.company);
        if (
          companyIds.includes(postCompanyId) ||
          companyIds.includes(res.company) ||
          (userResponse?.companies || []).includes(res.company)
        ) {
          toast.success('New Post Received from your company');
        }
      } catch (error) {
        console.error('Error fetching user details in handlePost:', error);
      }
    };

    const handleComment = (res) => {
      const userId = Cookies.get('userId');
      const createdBy = res.post.created_by;

      if (String(userId) === String(createdBy)) {
        toast.success('Someone commented on your post');
      }
    };

    const handleTag = (res) => {
      // Uncomment if you want tag notification
      // const userId = Cookies.get('userId');
      // const createdBy = res.post.created_by;
      // if (String(userId) === String(createdBy)) {
      //   toast.success('Someone tagged on your post');
      // }
    };

    const handleReply = (res) => {
      const userId = Cookies.get('userId');
      const createdBy = res.comment.created_by;

      if (String(userId) === String(createdBy)) {
        toast.success('Someone replied on your comment');
      }
    };

    const handleLike = (res) => {
      const userId = Cookies.get('userId');
      const createdBy = res.post.created_by;

      if (String(userId) === String(createdBy)) {
        toast.success(`${res.uerData?.name} liked your post`);
      }
    };

    socket.on('Post', handlePost);
    socket.on('Comment', handleComment);
    socket.on('Reply', handleReply);
    socket.on('Like', handleLike);
    socket.on('TagNotification', handleTag);

    return () => {
      socket.off('Post', handlePost);
      socket.off('Comment', handleComment);
      socket.off('Reply', handleReply);
      socket.off('Like', handleLike);
      socket.off('TagNotification', handleTag);
    };
  }, []);

  const renderLinks = (links) => {
    const filteredLinks = links.filter((link) => {
      // Allow Integration for Admin, Management, Marketing, and Inventory verticals
      if (link.name === 'Integration') {
        return (
          DesignationName === 'Admin' ||
          userVertical?.toLowerCase() === 'management' ||
          userVertical?.toLowerCase() === 'marketing' ||
          userVertical?.toLowerCase() === 'inventory'
        );
      }
      return true;
    });

    return filteredLinks.map((link, index) => (
      <li
        key={index}
        className={`flex flex-col justify-center items-start mb-2 p-3 ${
          isParentActive(selectedPath, link) && 'rounded-lg bg-bgSidebar'
        }`}>
        <div className="flex w-full justify-between items-center space-x-2">
          <div
            className="flex items-center space-x-1 cursor-pointer gap-2"
            onClick={() => {
              if (collapsed) {
                // Expand first, then navigate
                setCollapsed(false);
                setTimeout(() => handleLinkClick(link.path), 300); // match with transition duration
              } else {
                handleLinkClick(link.path);
              }
            }}>
            <img
              src={link.icon}
              alt={link.name}
              className="h-5 w-5 flex-shrink-0 transition-all duration-300 ease-in-out"
            />
            <div
              className={`text-white hover:text-gray-300 cursor-pointer whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out ${
                collapsed ? 'opacity-0 max-w-0' : 'opacity-100 max-w-[200px] ml-2'
              }`}>
              {link.name}
              {link.badge ? (
                <span className="ml-2 inline-flex min-w-[20px] items-center justify-center rounded-full bg-white px-1.5 text-[11px] font-semibold text-[#2A565E]">
                  {link.badge}
                </span>
              ) : null}
            </div>
          </div>

          {/* Drop down toggle icon */}
          {link?.children && !collapsed && (
            <img
              src={drop_down}
              alt="Drop down icon"
              className={`cursor-pointer transform transition-all duration-300 ease-in-out ${
                expandedItem === link.name ? 'rotate-180' : ''
              }`}
              onClick={(e) => {
                e.stopPropagation(); // prevent triggering parent onClick
                setExpandedItem(expandedItem === link.name ? '' : link.name);
              }}
            />
          )}
        </div>

        <div
          className={`flex flex-col ml-9 overflow-hidden transition-all duration-300 ease-in-out gap-2 ${
            expandedItem === link.name && !collapsed
              ? 'max-h-[1400px] opacity-100 mt-3'
              : 'max-h-0 opacity-0 mt-0 pointer-events-none'
          }`}>
          {link?.children?.map((item, idx) => {
            if (item?.group) {
              return (
                <span
                  key={`group-${item.group}-${idx}`}
                  className="pt-2 first:pt-0 text-[10px] font-semibold uppercase tracking-wide text-white/45">
                  {item.group}
                </span>
              );
            }
            const displayName = typeof item === 'object' ? item.displayName : item;
            const href = childHref(link.path, item);
            return (
              <span
                key={idx}
                className={`cursor-pointer ${
                  isChildActive(selectedPath, href, location.search) ? 'text-[#77BDC5]' : 'text-white'
                }`}
                onClick={() => handleLinkClick(href)}>
                {displayName}
              </span>
            );
          })}
        </div>
      </li>
    ));
  };

  return (
    <div
      className={`sidebar fixed z-[1000] top-0 left-0 bg-customBlue min-h-screen border-r border-gray-300
        ${collapsed ? 'w-[76px]' : 'w-64 md:w-64'}
       transition-all duration-300 ease-in-out
       md:block
      `}
      onMouseEnter={openOnHover}
      onMouseLeave={cancelHoverOpen}>
      <div className="flex items-center justify-between p-4 relative">
        <div className="relative h-[3.5rem] w-[11rem] flex items-center justify-center">
          <img
            src={logoIcon}
            alt="LogoIcon"
            className={`absolute top-0 left-0 transition-all duration-300 ease-in-out
              ${collapsed ? 'opacity-100 scale-100 h-[2.5rem] w-[3.3rem]' : 'opacity-0 scale-90 h-0 w-0'}
            `}
            style={{ objectFit: 'contain' }}
          />
          <img
            src={logo}
            alt="Logo"
            className={`absolute top-0 left-0 transition-all duration-300 ease-in-out
              ${collapsed ? 'opacity-0 scale-90 h-0 w-0' : 'opacity-100 scale-100 h-[3.5rem] w-[11rem]'}
            `}
            style={{ objectFit: 'contain' }}
          />
        </div>
        {role !== 'admin' && (
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="absolute top-2 right-[-0.6rem] w-6 h-6 flex items-center justify-center bg-[#2A565E] text-white rounded-full z-30"
            aria-label="Toggle Sidebar">
            <ChevronRight
              className={`w-4 h-4 transition-transform duration-300 ${!collapsed ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>

      <ul className="p-4 overflow-y-auto overflow-x-hidden hide-scrollbar max-h-[calc(100vh-5rem)] ">
        {renderLinks(links)}
      </ul>
    </div>
  );
};

export default Sidebar;

function pathOnly(href) {
  return String(href || '').split('?')[0];
}

function childHref(parentPath, item) {
  const pathSegment = typeof item === 'object' ? item.path : item;
  if (typeof pathSegment === 'string' && pathSegment.startsWith('/')) {
    return pathSegment;
  }
  return `${parentPath}/${String(pathSegment).toLowerCase()}`;
}

function isChildActive(selectedPath, href, search = '') {
  const base = pathOnly(href);
  // Two entries can open the same page with a different filter (Reports →
  // Employee / Labour Reports). Those must light up only for their own filter.
  const query = String(href || '').split('?')[1];
  if (query) {
    const want = new URLSearchParams(query);
    const have = new URLSearchParams(search);
    return selectedPath === base && [...want].every(([k, v]) => have.get(k) === v);
  }
  if (selectedPath === base) return true;
  if (base === '/builder/management/tasks' && selectedPath.startsWith('/builder/management/tasks/')) {
    return true;
  }
  if (href === '/builder/hr/employees' && selectedPath.startsWith('/builder/hr/employees/')) {
    return true;
  }
  if (href === '/builder/hr/payroll' && selectedPath.startsWith('/builder/hr/payroll/')) {
    return true;
  }
  return false;
}

function isParentActive(selectedPath, link) {
  if (
    link.children?.some(
      (item) => !item?.group && isChildActive(selectedPath, childHref(link.path, item))
    )
  ) {
    return true;
  }
  // Settled before the substring fallback, or Operations takes a labour screen
  // off Workforce on the strength of a shared URL prefix.
  if (ownedByWorkforce(selectedPath)) {
    return link.name === 'Workforce';
  }
  return Boolean(link.path) && selectedPath.includes(link.path);
}
